import React, { useState } from 'react';
import { Alert, Button, Card, Form, Select, Upload, Spin } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import styles from './batch-move.module.scss';
import api from '@services/api';
import { ApiError, Job, StbGroupExtended } from '@services/types';
import { MoveStbGroupBatchModeEnum, MoveStbGroupBatchRequest, StbGroupNameResponse } from '@openapi';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import { selectSearchFilter } from '@services/utils';

const { Option } = Select;

const Batch = () => {
  const [form] = Form.useForm<MoveStbGroupBatchRequest>();
  const [file, setFile] = React.useState<Blob>();
  const [stbGroup, setStbGroup] = useState<Array<StbGroupNameResponse>>([]);
  const [jobId, setJobId] = useState<Job['id']>();
  const [processButton, setProcessButton] = useState(true);
  const [spinner, setSpinner] = useState(false);

  React.useEffect(() => {
    api.stbGroups.getStbGroupsFilter().then((data) => {
      setStbGroup(data);
    });
  }, []);

  const handleFileChange = (options: UploadRequestOption) => {
    if (typeof options.onSuccess === 'function') {
      options.onSuccess('ok');
    }
    setFile(options.file as Blob);
  };
  const checkJobStatus = (jobId: Job['id']) => {
    const intervalId = setInterval(() => {
      api.jobs.getJob(jobId).then((data) => {
        if (data.status !== 'RUNNING') {
          clearInterval(intervalId);
          toast.error(data.errorMessage);
          setSpinner(false);
          if (data.status === 'ERROR') {
            setProcessButton(true);
          } else {
            setProcessButton(false);
          }
        }
      });
    }, 5000);
  };

  const handleSubmit = async (mode: MoveStbGroupBatchModeEnum) => {
    try {
      await form.validateFields();
      if (!file) {
        throw new Error('File not found');
      }
      if (mode === 'PROCESS') {
        await api.batchMove.exportBatch(form.getFieldValue('id'), file, mode).then((obj) => {
          setJobId(obj.etc.jobId);
          form.resetFields();
          toast.success('Export was completed successfully');
          setProcessButton(true);
        });
      } else {
        await api.batchMove.exportBatch(form.getFieldValue('id'), file, mode).then((data) => {
          toast.info(`Please wait while we run the job and perform a file check`);
          checkJobStatus(data.etc.jobId);
        });
      }
    } catch (error) {
      if (error instanceof ApiError) {
        toast.error(error?.error.message || 'Export failed to be completed');
        setSpinner(false);
      } else {
        toast.error('Export failed to be completed');
      }
    }
  };

  return (
    <Card title="Create move and provisioning job">
      {!jobId ? (
        <div>
          <Alert message="You can proceed only after checking and analyzing the results." type="info" showIcon />
          <Form className={styles.form} form={form}>
            <Form.Item
              className={styles.stbGroup}
              name="id"
              label="Destination group"
              rules={[
                {
                  required: true,
                  message: 'Please choose the destination group',
                },
              ]}
            >
              <Select allowClear showSearch filterOption={selectSearchFilter}>
                {stbGroup.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {`${item.name}(${item.deviceTypeName}, ${item.scope})`}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item
              className={styles.upload}
              rules={[
                {
                  required: true,
                  message: 'Please choose the file',
                },
              ]}
              name="file"
            >
              <Upload name="upload" accept=".csv" customRequest={handleFileChange} maxCount={1}>
                <Button type="default" icon={<FileAddOutlined />}>
                  Choose File
                </Button>
              </Upload>
            </Form.Item>
            <div className={styles.submit}>
              <Button
                className={styles.validate}
                onClick={() => {
                  handleSubmit(MoveStbGroupBatchModeEnum.Validate);
                  setSpinner(true);
                }}
                type="primary"
              >
                Test Input File
              </Button>
              {spinner && <Spin />}
              <Button
                disabled={processButton}
                className={styles.process}
                onClick={() => {
                  handleSubmit(MoveStbGroupBatchModeEnum.Process);
                }}
                type="primary"
              >
                Process
              </Button>
            </div>
          </Form>
        </div>
      ) : (
        <div style={{ display: 'grid', gridGap: '10px', justifyItems: 'center' }}>
          <Alert style={{ width: '100%' }} message="Operation successful" type="success" showIcon />
          <div style={{ display: 'grid', gridTemplateColumns: 'max-content max-content', gridGap: '10px' }}>
            <Button
              style={{ width: '250px' }}
              type="default"
              onClick={() => {
                setJobId(undefined);
              }}
            >
              Go to Batch Move
            </Button>
            <Button style={{ width: '250px' }} type="primary">
              <Link to={Navigation.JobInfo.replace(':id', jobId || '')}>Go to Job</Link>
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
};
export default React.memo(Batch);
